import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Heading,
  Avatar,
  Badge,
  Input,
  Select,
  Button,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Icon,
  Tooltip
} from '@chakra-ui/react';
import { FiSearch, FiFilter, FiUser, FiTrendingUp } from 'react-icons/fi';
import { useDataStore } from '../../contexts/DataStoreContext';
import { FighterProfile } from './FighterProfile';
import { LoadingSpinner } from '../Common/LoadingSpinner';

export const FighterList = () => {
  const { getAllFighters, getFilteredFighters, loading } = useDataStore();
  const [filteredFighters, setFilteredFighters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [weightClassFilter, setWeightClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedFighter, setSelectedFighter] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const weightClasses = [
    'Strawweight', 'Flyweight', 'Bantamweight', 'Featherweight',
    'Lightweight', 'Welterweight', 'Middleweight', 'Light Heavyweight',
    'Heavyweight', 'Super Heavyweight'
  ];

  useEffect(() => {
    applyFilters();
  }, [searchTerm, weightClassFilter, statusFilter, getAllFighters]);

  const applyFilters = () => {
    // Use DataStore's built-in filtering when possible
    const filtered = getFilteredFighters({
      searchTerm,
      weightClass: weightClassFilter,
      status: statusFilter
    });

    setFilteredFighters(filtered);
  };

  const handleFighterClick = (fighter) => {
    setSelectedFighter(fighter);
    onOpen();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setWeightClassFilter('');
    setStatusFilter('');
  };

  const FighterCard = ({ fighter }) => (
    <Card
      cursor="pointer"
      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
      transition="all 0.2s"
      onClick={() => handleFighterClick(fighter)}
    >
      <CardBody>
        <VStack spacing={3}>
          <Avatar
            size="lg"
            name={fighter.name}
            src={fighter.photoUrl}
            color="gray.700"
            bg="white"
          />

          <VStack spacing={1} textAlign="center">
            <Heading size="sm" color="white" noOfLines={1}>
              {fighter.name}
            </Heading>
            {fighter.nickname && (
              <Text fontSize="xs" color="gray.400" fontStyle="italic" noOfLines={1}>
                "{fighter.nickname}"
              </Text>
            )}
          </VStack>

          <VStack spacing={2} w="full">
            {fighter.weightClass && (
              <Badge colorScheme="blue" size="sm">
                {fighter.weightClass}
              </Badge>
            )}

            {fighter.record && (
              <Text fontSize="xs" color="gray.300" textAlign="center">
                {fighter.record}
              </Text>
            )}

            {fighter.gym && (
              <Text fontSize="xs" color="gray.400" textAlign="center" noOfLines={1}>
                {fighter.gym}
              </Text>
            )}
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );

  if (loading.fighters) {
    return <LoadingSpinner message="Loading fighters..." />;
  }

  return (
    <Box maxW="7xl" mx="auto" p={6}>
      {/* Header */}
      <VStack spacing={6} mb={8}>
        <Heading size="xl" textAlign="center" color="white">
          Fighter Roster
        </Heading>

        {/* Search and Filters */}
        <Card w="full">
          <CardBody>
            <VStack spacing={4}>
              <HStack w="full" spacing={4}>
                <InputGroup flex={2}>
                  <InputLeftElement>
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search fighters by name or nickname..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>

                <Select
                  placeholder="All Weight Classes"
                  value={weightClassFilter}
                  onChange={(e) => setWeightClassFilter(e.target.value)}
                  flex={1}
                >
                  {weightClasses.map(weightClass => (
                    <option key={weightClass} value={weightClass}>
                      {weightClass}
                    </option>
                  ))}
                </Select>

                <Button
                  variant="outline"
                  onClick={clearFilters}
                  isDisabled={!searchTerm && !weightClassFilter && !statusFilter}
                >
                  Clear
                </Button>
              </HStack>

              <HStack justify="space-between" w="full">
                <Text fontSize="sm" color="gray.300">
                  Showing {filteredFighters.length} of {getAllFighters().length} fighters
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Loading State */}
      {loading.fighters && <LoadingSpinner />}

      {/* Fighters Grid */}
      {!loading.fighters && filteredFighters.length > 0 ? (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing={6}>
          {filteredFighters.map(fighter => (
            <FighterCard key={fighter.id} fighter={fighter} />
          ))}
        </SimpleGrid>
      ) : !loading.fighters ? (
        <Card>
          <CardBody textAlign="center" py={10}>
            <VStack spacing={4}>
              <Icon as={FiUser} boxSize={12} color="gray.400" />
              <VStack spacing={2}>
                <Text color="gray.400" fontSize="lg">
                  No fighters found
                </Text>
                <Text color="gray.500" fontSize="sm">
                  {searchTerm || weightClassFilter
                    ? 'Try adjusting your search criteria'
                    : 'No fighters have been added yet'
                  }
                </Text>
              </VStack>
              {(searchTerm || weightClassFilter) && (
                <Button size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </VStack>
          </CardBody>
        </Card>
      ) : null}


      {/* Fighter Profile Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>Fighter Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedFighter && (
              <FighterProfile fighter={selectedFighter} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FighterList;
